import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridSortModel,
  type GridRowParams,
} from '@mui/x-data-grid';
import type { Study } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { ColumnConfigPopup } from './ColumnConfigPopup';
import { type ColumnConfig, loadColumnConfig } from './columnConfig';

interface StudyDataGridProps {
  studies: Study[];
  loading: boolean;
  onSelectionChange: (uids: string[]) => void;
  onDoubleClick: (study: Study, event?: React.MouseEvent) => void;
  onContextMenu: (event: React.MouseEvent, study: Study) => void;
  selectedUids: string[];
  /** Server-side sort model */
  sortModel: GridSortModel;
  /** Called when user clicks column header to sort */
  onSortModelChange: (model: GridSortModel) => void;
}

/**
 * All possible column definitions — keyed by field name.
 * Width/flex settings match the GWT default layout.
 */
const COLUMN_DEFS: Record<string, Omit<GridColDef<Study>, 'field'>> = {
  name:        { headerName: 'Patient Name', flex: 1, minWidth: 150,
                 valueGetter: (_value: unknown, row: Study) => row.niceName || row.name || '' },
  id:          { headerName: 'Patient ID', width: 100 },
  sex:         { headerName: 'Sex', width: 40 },
  dob:         { headerName: 'DOB', width: 90 },
  status:      { headerName: 'Status', width: 90 },
  acc:         { headerName: 'Acc #', width: 100 },
  mod:         { headerName: 'Mod', width: 60 },
  bod:         { headerName: 'Body', width: 80 },
  date:        { headerName: 'Date', width: 90 },
  physician:   { headerName: 'Physician', flex: 1, minWidth: 100,
                 valueGetter: (_value: unknown, row: Study) => row.phyName || row.phy || '' },
  ae:          { headerName: 'AE', width: 80 },
  descr:       { headerName: 'Description', flex: 1, minWidth: 120 },
  radiologist: { headerName: 'Radiologist', flex: 1, minWidth: 100,
                 valueGetter: (_value: unknown, row: Study) => row.radiologistName || row.radiologist || '' },
  comment:     { headerName: 'Comment', flex: 1, minWidth: 80 },
  dicomCount:  { headerName: '#', width: 50, type: 'number' },
  faxCount:    { headerName: 'Fax', width: 50, type: 'number' },
  siteName:    { headerName: 'Site', width: 90 },
  history:     { headerName: 'History', flex: 1, minWidth: 100 },
  reason:      { headerName: 'Reason', flex: 1, minWidth: 100 },
  finalRead:   { headerName: 'Final Read', width: 90 },
};

/** Convert ColumnConfig[] (visible only) to GridColDef[] */
function toGridColumns(configs: ColumnConfig[]): GridColDef<Study>[] {
  return configs
    .filter((c) => c.visible)
    .map((c) => ({
      field: c.field,
      ...(COLUMN_DEFS[c.field] ?? { headerName: c.headerName, width: c.width ?? 100 }),
    }));
}

/**
 * GWT-matching study data grid.
 *
 * Row styling:
 *   - Default rows: #D8DFE6
 *   - Odd rows: #E0E5EB
 *   - Matches .brit-studygrid-default / .brit-studygrid-odd
 */
export const StudyDataGrid = ({
  studies,
  loading,
  onSelectionChange,
  onDoubleClick,
  onContextMenu,
  selectedUids,
  sortModel,
  onSortModelChange,
}: StudyDataGridProps) => {
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(() => loadColumnConfig());
  const [colConfigAnchor, setColConfigAnchor] = useState<HTMLElement | null>(null);
  // Track the last-clicked row index for Shift+click range selection
  const lastClickedIndex = useRef<number>(-1);

  // Build GridColDef[] from current column config
  const columns = useMemo(() => toGridColumns(columnConfig), [columnConfig]);

  // Build a Set for fast lookup of selected UIDs
  const selectedSet = useMemo(() => new Set(selectedUids), [selectedUids]);

  /** Manual row click handler — supports Ctrl+click toggle and Shift+click range */
  const handleRowClick = useCallback(
    (params: GridRowParams<Study>, event: React.MouseEvent) => {
      const uid = params.row.uid;
      const idx = studies.findIndex((s) => s.uid === uid);

      if (event.ctrlKey || event.metaKey) {
        // Toggle this row
        const next = selectedSet.has(uid)
          ? selectedUids.filter((u) => u !== uid)
          : [...selectedUids, uid];
        onSelectionChange(next);
        lastClickedIndex.current = idx;
      } else if (event.shiftKey && lastClickedIndex.current >= 0) {
        // Range select from last click to this click
        const start = Math.min(lastClickedIndex.current, idx);
        const end = Math.max(lastClickedIndex.current, idx);
        const rangeUids = studies.slice(start, end + 1).map((s) => s.uid);
        // Merge with existing selection (union)
        const merged = new Set([...selectedUids, ...rangeUids]);
        onSelectionChange(Array.from(merged));
      } else {
        // Plain click — select only this row
        onSelectionChange([uid]);
        lastClickedIndex.current = idx;
      }
    },
    [studies, selectedUids, selectedSet, onSelectionChange],
  );

  const handleRowDoubleClick = useCallback(
    (params: GridRowParams<Study>, event: React.MouseEvent) => {
      onDoubleClick(params.row, event);
    },
    [onDoubleClick],
  );

  const handleColumnHeaderContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setColConfigAnchor(event.currentTarget as HTMLElement);
    },
    [],
  );

  /** Handle right-click anywhere in the grid */
  const handleGridContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;

      // Column header right-click → column config popup
      if (target.closest('.MuiDataGrid-columnHeader')) {
        handleColumnHeaderContextMenu(e);
        return;
      }

      // Row right-click → study context menu
      const rowEl = target.closest('.MuiDataGrid-row');
      if (rowEl) {
        const uid = rowEl.getAttribute('data-id');
        const study = uid ? studies.find((s) => s.uid === uid) : undefined;
        if (study) {
          e.preventDefault();
          onContextMenu(e, study);
        }
      }
    },
    [studies, onContextMenu, handleColumnHeaderContextMenu],
  );

  const handleColumnsChange = useCallback((newConfig: ColumnConfig[]) => {
    setColumnConfig(newConfig);
  }, []);

  // Ctrl+A / Cmd+A → select all rows
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        // Only capture if focus is inside the grid
        const target = e.target as HTMLElement;
        if (target.closest('.MuiDataGrid-root')) {
          e.preventDefault();
          onSelectionChange(studies.map((s) => s.uid));
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [studies, onSelectionChange]);

  return (
    <Box
      sx={{ height: '100%', width: '100%' }}
      onContextMenu={handleGridContextMenu}
    >
      <DataGrid
        rows={studies}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.uid}
        // Disable MUI's built-in selection (community only supports single)
        disableRowSelectionOnClick
        // Manual row click for multi-select
        onRowClick={handleRowClick}
        // Apply custom class to selected rows
        getRowClassName={(params) => selectedSet.has(params.id as string) ? 'brit-row-selected' : ''}
        // Sorting (server-side via ORDER WhereBeans)
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingMode="server"
        // Pagination disabled — we handle it ourselves in the bottom bar
        hideFooter
        // Row double-click
        onRowDoubleClick={handleRowDoubleClick}
        // Styling
        density="compact"
        rowHeight={24}
        columnHeaderHeight={28}
        disableColumnMenu={false}
        sx={{
          border: 'none',
          fontFamily: 'Arial, sans-serif',
          fontSize: 12,
          // Alternating row colors matching GWT
          '& .MuiDataGrid-row': {
            '&:nth-of-type(odd)': {
              backgroundColor: gwtStyles.gridRowDefault,
            },
            '&:nth-of-type(even)': {
              backgroundColor: gwtStyles.gridRowOdd,
            },
            '&.brit-row-selected': {
              backgroundColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            },
            '&:hover': {
              backgroundColor: `${gwtStyles.gridRowDefault}dd`,
            },
          },
          // Header styling
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: gwtStyles.tabContent,
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontSize: 11,
            fontWeight: 600,
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
          // Cell styling
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            py: 0,
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
        }}
      />

      {/* Column configuration popup — triggered by right-click on column header */}
      <ColumnConfigPopup
        anchorEl={colConfigAnchor}
        onClose={() => setColConfigAnchor(null)}
        columns={columnConfig}
        onColumnsChange={handleColumnsChange}
      />
    </Box>
  );
};
